import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getSymptomCorrelations } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { useLanguage } from '../contexts/LanguageContext';
import {
    TrendingUp, Moon, Droplets, Dumbbell, Calendar,
    Sparkles, AlertTriangle, ChevronDown, ChevronUp, Lightbulb
} from 'lucide-react';

export function HealthCorrelations() {
    const { t, language } = useLanguage();
    const [correlations, setCorrelations] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(true);
    const [selectedPeriod, setSelectedPeriod] = useState(30);

    useEffect(() => {
        loadCorrelations();
    }, [selectedPeriod]);

    const loadCorrelations = async () => {
        setLoading(true);
        try {
            const response = await getSymptomCorrelations(selectedPeriod);
            setCorrelations(response.data);
        } catch (error) {
            console.error('Failed to load correlations:', error);
        } finally {
            setLoading(false);
        }
    };

    const getCorrelationIcon = (type) => {
        switch (type) {
            case 'sleep_symptoms': return Moon;
            case 'hydration_symptoms': return Droplets;
            case 'exercise_symptoms': return Dumbbell;
            case 'weekly_pattern': return Calendar;
            default: return TrendingUp;
        }
    };

    const getCorrelationColor = (strength) => {
        if (strength === 'strong') return 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 border-rose-200';
        return 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 border-amber-200';
    };

    if (loading) {
        return (
            <Card>
                <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-6 w-6 rounded" />
                        <Skeleton className="h-6 w-48" />
                    </div>
                </CardHeader>
                <CardContent className="space-y-4">
                    <Skeleton className="h-24 w-full rounded-xl" />
                    <Skeleton className="h-24 w-full rounded-xl" />
                </CardContent>
            </Card>
        );
    }

    if (!correlations || !correlations.has_enough_data) {
        return (
            <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-8 text-center">
                    <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-4">
                        <TrendingUp className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <p className="text-muted-foreground max-w-sm">
                        {t('correlations_no_data')}
                    </p>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card className="overflow-hidden">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-violet-500" />
                        {t('correlations_title')}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <div className="flex rounded-full bg-muted p-1">
                            {[7, 14, 30].map((days) => (
                                <Button
                                    key={days}
                                    variant={selectedPeriod === days ? "default" : "ghost"}
                                    size="sm"
                                    className="rounded-full h-7 px-3 text-xs"
                                    onClick={() => setSelectedPeriod(days)}
                                >
                                    {days}d
                                </Button>
                            ))}
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setExpanded(!expanded)}
                        >
                            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        </Button>
                    </div>
                </div>
                <p className="text-sm text-muted-foreground">
                    {language === 'de' 
                        ? 'Nanouk analysiert Zusammenhänge zwischen deinen Symptomen, Schlaf, Ernährung und Fitness.'
                        : 'Nanouk analyzes connections between your symptoms, sleep, nutrition and fitness.'}
                    {' • '}{correlations.total_symptoms || 0} {language === 'de' ? 'Symptome' : 'symptoms'}
                </p>
            </CardHeader>

            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        <CardContent className="space-y-4">
                            {/* AI Insights */}
                            {correlations.insights && correlations.insights.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="p-4 rounded-xl bg-gradient-to-r from-violet-50 to-purple-50 dark:from-violet-900/20 dark:to-purple-900/20 border border-violet-200 dark:border-violet-800"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-800 flex items-center justify-center flex-shrink-0">
                                            <Lightbulb className="w-5 h-5 text-violet-600 dark:text-violet-300" />
                                        </div>
                                        <div>
                                            <p className="font-medium text-violet-700 dark:text-violet-300 mb-1">
                                                {t('correlations_insights')}
                                            </p>
                                            <p className="text-sm text-violet-600/80 dark:text-violet-400/80">
                                                {correlations.insights[0].text}
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {/* Correlations List */}
                            {correlations.correlations && correlations.correlations.length > 0 ? (
                                <div className="space-y-3">
                                    {correlations.correlations.map((corr, index) => {
                                        const Icon = getCorrelationIcon(corr.type);
                                        const title = language === 'de' ? corr.title_de : corr.title_en;
                                        const description = language === 'de' ? corr.description_de : corr.description_en;
                                        const recommendation = language === 'de' ? corr.recommendation_de : corr.recommendation_en;

                                        return (
                                            <motion.div
                                                key={corr.type}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: index * 0.1 }}
                                                className={`p-4 rounded-xl border ${getCorrelationColor(corr.strength)}`}
                                            >
                                                <div className="flex items-start gap-3">
                                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                                                        corr.strength === 'strong' 
                                                            ? 'bg-rose-200 dark:bg-rose-800' 
                                                            : 'bg-amber-200 dark:bg-amber-800'
                                                    }`}>
                                                        <Icon className="w-5 h-5" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <p className="font-semibold">{title}</p>
                                                            <Badge variant="outline" className="text-xs">
                                                                {corr.strength === 'strong' 
                                                                    ? t('correlations_strong') 
                                                                    : t('correlations_moderate')}
                                                            </Badge>
                                                        </div>
                                                        <p className="text-sm opacity-90 mb-2">
                                                            {description}
                                                        </p>
                                                        <div className="flex items-center gap-2 text-xs">
                                                            <AlertTriangle className="w-3 h-3" />
                                                            <span className="font-medium">{t('correlations_recommendation')}:</span>
                                                            <span className="opacity-80">{recommendation}</span>
                                                        </div>
                                                        <p className="text-xs opacity-60 mt-1">
                                                            {corr.data_points} {t('correlations_data_points')}
                                                        </p>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-center py-6 text-muted-foreground">
                                    <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                    <p className="text-sm">
                                        {language === 'de' 
                                            ? 'Keine signifikanten Korrelationen gefunden. Tracke weiter!' 
                                            : 'No significant correlations found. Keep tracking!'}
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </motion.div>
                )}
            </AnimatePresence>
        </Card>
    );
}
